<script>
	import { onMount } from "svelte";
	import { fly } from "svelte/transition";
	import { scaleOrdinal } from "d3-scale";
	import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
	import centerOfMass from "@turf/center-of-mass";

	import Buttons from "./components/Buttons.svelte";
	import Switch from "./components/Switch.svelte";
	import Search from "./components/Search/Search.svelte";
	import InfoBox from "./components/InfoBox.svelte";
	import MediaQuery from "./components/MediaQuerySsr.svelte";
	import Map from "./map/map";

	import { loadCSV, loadJSON, loadMapbox, loadTopojson, scrollTo, convertToGeoJSON, getStripes } from "./utils";

	// ///////////////////////////////////////////////////////////
	// props
	// ///////////////////////////////////////////////////////////
	export let name;
	export let paywall = false;

	// ///////////////////////////////////////////////////////////
	// constants and definitions
	// ///////////////////////////////////////////////////////////
	let map;
	let tooltip;

	let mapZoom;

	let mapActivated = false;

	let mapLoaded = paywall;

	let labelsLorsId = "labels-lors";
	let labelsLors = [];
	let labelsBezirke = [];

	const initialSelectedPoint = { lng: 13.388198546620572, lat: 52.55289914372827 };

	const initialBounds = [
		[13.761167660223435, 52.676262238895134],
		[13.073611526282605, 52.33488609760633],
	];

	const initialBoundsTablet = [
		[13.211867193817284, 52.27179558551268],
		[13.899423327757603, 52.735101645391296],
	];

	const maxBounds = [
		[12.758595905118852, 52.227746928806766],
		[14.076183281386392, 52.782315046335015],
	];

	const baseOpacity = 0.7;
	const baseOpacityLuftbilder = 0.4;

	const colorNoData = "#b9b9b9";
	const labelNoData = "keine Daten";
	let highlightingColor = "#000";

	let showLuftbilder = false;
	const luftbilderId = "luftbilder";

	const detailedGreeneryZoom = 11;
	const expressionDetailedGreeneryZoomOpacity = ["step", ["zoom"], baseOpacity, detailedGreeneryZoom, 0];

	let choroplethOpacity = expressionDetailedGreeneryZoomOpacity;

	let geoBelastungen;
	let topoBelastungen;
	let dataBelastungen;
	let geoBezirke;
	let geoRing;
	let dataFlaechen;

	const geoData = {
		issues: {
			id: "issues",
			nameKey: "plr_name",
			idKey: "plr",
		},
		greenery: {
			id: "greenery",
			nameKey: "",
			idKey: "id",
		},
	};

	const bezirke = {
		"01": "Mitte",
		"02": "Friedrichshain-Kreuzberg",
		"03": "Pankow",
		"04": "Charlottenburg-Wilmersdorf",
		"05": "Spandau",
		"06": "Steglitz-Zehlendorf",
		"07": "Tempelhof-Schöneberg",
		"08": "Neukölln",
		"09": "Treptow-Köpenick",
		"10": "Marzahn-Hellersdorf",
		"11": "Lichtenberg",
		"12": "Reinickendorf",
	};

	const markerData = [
		{ n: "fernsehturm", lat: 52.52084869043665, lon: 13.409393064444167 },
		// { n: "oberbaum", lat: 52.50177747640686, lon: 13.445625367869344 },
		// { n: "bbtor", lat: 52.51626139984922, lon: 13.377703623983129 },
		// { n: "siegessaeule", lat: 52.5144815459746, lon: 13.35010685 },
		// { n: "kaiserwilhelm", lat: 52.50484564808263, lon: 13.335195025119821 },
		// {
		// 	n: "ber",
		// 	lat: 52.36243659135566,
		// 	lon: 13.503429136808114,
		// 	label: "BER",
		// },
		// {
		// 	n: "grunewald",
		// 	lat: 52.47488120218827,
		// 	lon: 13.222450027908256,
		// 	label: "Grunewald",
		// },
		// {
		// 	n: "tegel",
		// 	lat: 52.55724116699942,
		// 	lon: 13.288008246555517,
		// 	label: "TXL",
		// },
		// {
		// 	n: "mueggelsee",
		// 	lat: 52.43760332884931,
		// 	lon: 13.648089557109495,
		// 	label: "Müggelsee",
		// },
	];

	const environmentalButtonOptions = [
		{ value: "greenery", label: "Grünversorgung", color: "#4E891380", icon: "preview_gruen.png" },
		{ value: "noise", label: "Lärm", color: "#E97AED80", icon: "preview_laerm.png" },
		{ value: "air", label: "Luftverschmutzung", color: "#FA931A80", icon: "preview_luft.png" },
		{ value: "thermic", label: "Hitze", color: "#FF564980", icon: "preview_hitze.png" },
		{ value: "issues_total", label: "Gesamtwertung", color: "#d9d9d980", icon: "preview_index.png", sep: true },
	];

	const socialButtonOptions = [
		// { value: "social_all", label: "alle" },
		{ value: "social_low", label: "niedrig" },
		{ value: "social_medium", label: "mittel" },
		{ value: "social_high", label: "hoch" },
	];

	const issues = [
		{
			id: "greenery",
			geo: geoData.issues,
			key: "gruenfl",
			label: "Grünflächenversorgung",
			icon: "gruenfl",
			values: [
				{ value: "keine", color: "#47261A" },
				{ value: "schlecht", color: "#CD8365" },
				{ value: "mittel", color: "#A2D74E" },
				{ value: "gut", color: "#4E8913" },
			],
		},
		{
			id: "noise",
			geo: geoData.issues,
			key: "laerm",
			label: "Lärmbelastung",
			icon: "laerm",
			values: [
				{ value: "gering", color: "#E9C7F9" },
				{ value: "mittel", color: "#E97AED" },
				{ value: "hoch", color: "#9C14C9" },
			],
		},
		{
			id: "air",
			geo: geoData.issues,
			key: "luft",
			label: "Luftbelastung",
			icon: "luft",
			values: [
				{ value: "gering", color: "#FFEB7F" },
				{ value: "mittel", color: "#FA931A" },
				{ value: "hoch", color: "#CB3900" },
			],
		},
		{
			id: "thermic",
			geo: geoData.issues,
			key: "bioklima",
			label: "Hitzebelastung",
			icon: "hitze",
			values: [
				{ value: "gering", color: "#FFA184" },
				{ value: "mittel", color: "#FF5649" },
				{ value: "hoch", color: "#AE1831" },
			],
		},
	];

	const issuesTotalKey = "issues_total";
	const issuesAndTotal = [
		...issues,
		{
			id: issuesTotalKey,
			geo: geoData.issues,
			key: issuesTotalKey,
			label: "Anzahl hoher Belastungen",
			values: [
				{ value: 0, color: "#B3F8A7" },
				{ value: 1, color: "#CED832" },
				{ value: 2, color: "#FF8B2D" },
				{ value: 3, color: "#CC2929" },
				{ value: 4, color: "#750500" },
			],
		},
	];

	const greeneryDetailed = {
		id: "greenery_detailed",
		geo: geoData.greenery,
		key: "gruen",
		label: "Grünflächenversorgung",
		values: [
			{ value: "0", color: "#47261A" },
			{ value: "1", color: "#CD8365" },
			{ value: "2", color: "#A2D74E" },
			{ value: "3", color: "#4E8913" },
		],
	};

	const issuesAndTotalById = {};
	issuesAndTotal.forEach((e) => {
		issuesAndTotalById[e.id] = e;
	});

	const socialStatus = {
		id: "social_status",
		key: "soz_index",
		label: "Sozialstatus",
		infobox:
			"Der Sozial&shy;status eines Kiezes errechnet sich aus den Indi&shy;katoren Arbeits&shy;losig&shy;keit, Empfang von Transfer&shy;leist&shy;ungen und Kinder&shy;armut. Grundlage für die Indi&shy;katoren ist das Monitoring Soziale Stadt&shy;ent&shy;wicklung (MSS) von 2021.",
		translate: (value) => {
			const mapping = {
				"niedriger/sehr niedriger Statusindex": "social_low",
				"mittlerer Statusindex": "social_medium",
				"hoher Statusindex": "social_high",
			};
			return mapping[value];
		},
		labels: {
			social_low: "niedrig",
			social_medium: "mittel",
			social_high: "hoch",
		},
	};

	const assessments = {
		bad: {
			color: "#EA8F7B",
			icon: "bad",
		},
		mid: {
			color: "#EEB000",
			icon: "mid",
		},
		good: {
			color: "#76C175",
			icon: "good",
		},
		null: {
			color: colorNoData,
			icon: null,
		},
	};
	assessments.gering = assessments.good;
	assessments.schlecht = assessments.bad;
	assessments.mittel = assessments.mid;
	assessments.hoch = assessments.bad;
	assessments.gut = assessments.good;

	// ///////////////////////////////////////////////////////////
	// init colors
	// ///////////////////////////////////////////////////////////
	const colors = {};
	[...issuesAndTotal, greeneryDetailed].forEach((e) => {
		if (!colors[e.id]) colors[e.id] = {};
		colors[e.id].scale = scaleOrdinal()
			.domain([...e.values.map((d) => d.value)])
			.range([...e.values.map((d) => d.color)])
			.unknown(null);
	});

	// ///////////////////////////////////////////////////////////
	// ui bound parameters
	// ///////////////////////////////////////////////////////////
	export let elemMap;

	let selectedIssue = "greenery";
	let selectedSocials = ["social_low", "social_medium", "social_high"];
	let currentDataColors;
	let currentGeoData;
	let currentGeometry;
	let selectedRegion;
	let selectedPoint;
	let hoveredRegion;

	// ///////////////////////////////////////////////////////////
	// update geo data
	// ///////////////////////////////////////////////////////////
	$: currentGeoData = issuesAndTotalById[selectedIssue].geo;
	$: if (mapActivated) {
		currentGeometry = currentGeoData.data;

		currentGeometry.features.forEach((f) => {
			// f.properties.filtered = selectedSocial !== "social_all" && f.properties[socialStatus.id] !== selectedSocial;
			// hack: wenn alle aktiviert auch die Kieze anzeigen die keinen sozialen Status haben (gewerbegebiete zB)
			if (selectedSocials.length === 3) {
				f.properties.filtered = false;
			} else {
				f.properties.filtered = !selectedSocials.includes(f.properties[socialStatus.id]);
			}
		});
		if (mapActivated) {
			if (map.map.getLayer(currentGeoData.id)) map.setChoroplethData(currentGeoData.id, [0], {});
			if (map.map.getLayer(geoData.greenery.id))
				if (selectedSocials.length === 3) {
					map.map.setFilter(geoData.greenery.id, null);
				} else {
					map.map.setFilter(geoData.greenery.id, ["in", "social", ...selectedSocials]);
				}

			if (selectedIssue === "greenery") map.showLayer(geoData.greenery.id);
			else map.hideLayer(geoData.greenery.id);

			if (map.map.getLayer(geoData.issues.id))
				map.map.setPaintProperty(geoData.issues.id, "fill-opacity", choroplethOpacity);
		}
	}

	// ///////////////////////////////////////////////////////////
	// update data colors
	// ///////////////////////////////////////////////////////////
	const getDataColors = (issue) => {
		if (!colors[issue].data) {
			colors[issue].data = currentGeometry.features.reduce(
				(o, f) => ({
					...o,
					[f.properties[currentGeoData.idKey]]: colors[issue].scale(f.properties[issuesAndTotalById[issue].id]),
				}),
				{}
			);
		}
		return colors[issue].data;
	};

	$: if (mapActivated) {
		currentDataColors = getDataColors(selectedIssue);

		if (mapActivated) {
			if (map.map.getLayer(currentGeoData.id))
				map.setChoroplethData(currentGeoData.id, [0], { colors: currentDataColors });
		}
	}

	// ///////////////////////////////////////////////////////////
	// set opacity
	// ///////////////////////////////////////////////////////////
	$: luftbilderChoroplethOpacity = showLuftbilder ? baseOpacityLuftbilder : baseOpacity;
	$: choroplethOpacity =
		selectedIssue === "greenery" ? expressionDetailedGreeneryZoomOpacity : luftbilderChoroplethOpacity;

	// ///////////////////////////////////////////////////////////
	// show/hide luftbilder
	// ///////////////////////////////////////////////////////////
	$: highlightingColor = showLuftbilder ? "#fff" : "#000";

	$: if (mapActivated) {
		if (showLuftbilder) {
			map.showLayer(luftbilderId);
		} else {
			map.hideLayer(luftbilderId);
		}
		if (map.map.getLayer(geoData.issues.id))
			map.map.setPaintProperty(geoData.issues.id, "fill-opacity", choroplethOpacity);
		if (map.map.getLayer(geoData.greenery.id))
			map.map.setPaintProperty(geoData.greenery.id, "fill-opacity", luftbilderChoroplethOpacity);

		const idHover = `${geoData.issues.id}-hovering`;
		const idSelected = `${geoData.issues.id}-selected`;

		if (map.map.getLayer(idHover)) map.map.setPaintProperty(idHover, "line-color", highlightingColor);
		if (map.map.getLayer(idSelected)) map.map.setPaintProperty(idSelected, "line-color", highlightingColor);

		map.updateChoroplethOpts(geoData.issues.id, {
			hoveringBorderColor: highlightingColor,
			selectedBorderColor: highlightingColor,
		});
	}

	// ///////////////////////////////////////////////////////////
	// data processing
	// ///////////////////////////////////////////////////////////
	const checkIssuesTotal = (data) => {
		let x = 0;
		issues.forEach((i) => {
			if (["hoch", "schlecht"].indexOf(data[i.key]) !== -1) x += 1;
		});
		return x;
	};

	const processData = () => {
		geoBelastungen.features.forEach((f) => {
			const id = f.properties[geoData.issues.idKey];
			const data = dataBelastungen.find((d) => d[geoData.issues.idKey] === id);

			f.properties.name = data[geoData.issues.nameKey];

			issues.forEach((i) => {
				f.properties[i.id] = data[i.key] || null;
			});

			const center = centerOfMass(f).geometry.coordinates;
			f.properties.centerLng = center[0];
			f.properties.centerLat = center[1];

			f.properties.bezirk = data.Bezirk;
			f.properties[socialStatus.id] = socialStatus.translate(data[socialStatus.key]);
			f.properties[issuesTotalKey] = checkIssuesTotal(data);

			labelsLors.push({
				name: `${f.properties.name}`,
				lnglat: [f.properties.centerLng, f.properties.centerLat],
			});
		});

		geoBezirke.features.forEach((f) => {
			const center = centerOfMass(f).geometry.coordinates;
			f.properties.centerLng = center[0];
			f.properties.centerLat = center[1];

			f.properties.name = bezirke[f.properties["BEZ"]];

			labelsBezirke.push({
				name: `${f.properties.name}`,
				lnglat: [f.properties.centerLng, f.properties.centerLat],
			});
		});
	};

	// ///////////////////////////////////////////////////////////
	// remove label for hovered/selected region
	// ///////////////////////////////////////////////////////////
	$: if (mapActivated) {
		labelsLors.forEach((l) => {
			l.opacity =
				(hoveredRegion && l.name === hoveredRegion.name) || (selectedRegion && l.name === selectedRegion.name) ? 0 : 1;
		});

		if (map.map.getLayer(labelsLorsId)) map.updateLabels(labelsLors, labelsLorsId);
	}
	// ///////////////////////////////////////////////////////////
	// initialize map
	// ///////////////////////////////////////////////////////////

	let tooltipHovered = false;

	const choroplethOpts = {
		geoType: "geojson",
		borderColor: "#FFF",
		borderWidth: 0.5,
		borderOpacity: 1,
		opacity: choroplethOpacity,
		dataType: "colors",
		colors: currentDataColors,
		data: [0],
		interactive: true,
		zoomOnClick: true,
		zoomLevel: 12.7,
		hoveringBorderColor: highlightingColor,
		hoveringBorderWidth: 3,
		selectedBorderColor: highlightingColor,
		selectedBorderWidth: 3,
		filteredColor: "transparent",
		filteredBorderColor: "transparent",
		choroplethClickCallback: (f, filtered, lngLat) => {
			if (!filtered) selectPoint(lngLat, f);
		},
		backgroundClickCallback: () => {
			clearPoint();
		},
		mousemoveCallback: (f, filtered) => {
			if (isTouch) return;
			hoveredRegion = f.properties;
			addPopup(f);
			updateFlaeche(hoveredRegion.plr);
		},
		mouseleaveCallback: () => {
			setTimeout(() => {
				if (!tooltipHovered) {
					hoveredRegion = undefined;
					removePopup();
					if (selectedRegion) addPopup(selectedRegion);
				}
			});
		},
	};

	let isTouch = false;

	onMount(async () => {
		if (paywall) return;

		const promises = [
			loadCSV(`${PATH}assets/data/belastungen.csv`),
			loadJSON(`${PATH}assets/data/belastungen_orig_without_props_sim.topo.json`),
			loadJSON(`${PATH}assets/data/berlin_bezirke.geo.json`),
			loadJSON(`${PATH}assets/data/ring.geo.json`),
			loadJSON(`${PATH}assets/data/flaechen.json`),
			loadTopojson(),
			loadMapbox(),
		];

		function checkTouch() {
			isTouch = true;
			document.querySelector("body").removeEventListener("touchstart", checkTouch);
		}
		document.querySelector("body").addEventListener("touchstart", checkTouch);

		[dataBelastungen, topoBelastungen, geoBezirke, geoRing, dataFlaechen] = await Promise.all(promises);

		geoBelastungen = convertToGeoJSON(topoBelastungen);
		processData();
		geoData.issues.data = geoBelastungen;

		currentGeometry = currentGeoData.data;
		currentDataColors = getDataColors(selectedIssue);

		map = new Map(elemMap, {
			useFranklin: true,
			initialBounds: platform === "tablet" ? initialBoundsTablet : initialBounds,
			maxBounds,
			controls: true,
			controlsLeft: true,
			scale: true,
			popup: false,
			popupShift: false,
			labels: true,
			// style: "wahl",
			styleJson: `${PATH}assets/style.json`,
			hideBackgroundMap: false,
		});

		map.activated
			.then(() => map.loaded)
			.then(() => {
				const REFLAYER = map.firstLabelLayer;

				map.hideAttribution();

				mapZoom = map.map.getZoom();
				map.map.on("zoom", () => {
					mapZoom = map.map.getZoom();
				});

				map.map.loadImage(getStripes("#ccc", 4, "#fff"), (error, image) => {
					map.map.addImage("stripes-nodata", image);
				});

				// luftbilder
				map.map.addSource(luftbilderId, {
					type: "raster",
					url: "https://interaktiv.tagesspiegel.de/maptiles/luftbilder_2022/tiles.json",
				});

				map.map.addLayer(
					{
						id: luftbilderId,
						source: luftbilderId,
						type: "raster",
					},
					REFLAYER
				);

				// dataset for all issues
				map.addChoropleth(geoData.issues.data, {
					...choroplethOpts,
					id: geoData.issues.id,
					identifier: geoData.issues.idKey,
					colors: currentDataColors,
					noDataColor: "#fff",
					noDataPattern: "stripes-nodata",
					beforeLayer: REFLAYER,
					zoomPadding: () => {
						return {
							top: 0,
							left: 0,
							// bottom: platform === "mobile" ? 150 : 0,
							// right: platform === "mobile" ? 0 : 290,
							bottom: window.innerWidth <= 544 ? 150 : 0,
							right: window.innerWidth <= 544 ? 0 : 290,
						};
					},
				});

				// detailed data set for greenery
				map.map.addSource("greenery", {
					type: "vector",
					url: "https://interaktiv.tagesspiegel.de/maptiles/gruenflaechen/tiles.json",
				});

				map.map.addLayer(
					{
						id: "greenery",
						source: "greenery",
						"source-layer": "gruenversorgung_orig_nicegeo",
						type: "fill",
						minzoom: detailedGreeneryZoom,
						paint: {
							"fill-opacity": 0.7,
							"fill-color": [
								"match",
								["get", "gruen"],
								3,
								greeneryDetailed.values[3].color,
								2,
								greeneryDetailed.values[2].color,
								1,
								greeneryDetailed.values[1].color,
								0,
								greeneryDetailed.values[0].color,
								"transparent",
							],
						},
					},
					REFLAYER
				);

				// bezirke
				map.addChoropleth(geoBezirke, {
					geoType: "geojson",
					id: "bezirke",
					borderColor: "#FFF",
					borderWidth: 3,
					interactive: false,
					opacity: 0,
					beforeLayer: REFLAYER,
				});

				// labels

				const labelsOpts = {
					minZoom: 12.5,
					maxZoom: 14.5,
					fontSize: 12,
					color: "#fff",
					haloWidth: 1,
					haloColor: "#000",
					letterSpacing: 0.015,
					font: "Franklin Gothic Medium",
				};

				map.addLabels(labelsLors, {
					...labelsOpts,
					id: labelsLorsId,
					textAllowOverlap: true,
					textAnchor: "top",
				});

				map.addLabels(labelsBezirke, {
					...labelsOpts,
					id: "labels-bezirke",
					minZoom: 9,
					maxZoom: 11.5,
				});

				showBerlinMarkers();
				showRing();

				mapActivated = true;

				requestAnimationFrame(() => {
					map.map.moveLayer("water", "bezirke");
				});

				map.map.once("idle", () => {
					mapLoaded = true;

					// preselect a point
					selectPoint(initialSelectedPoint, undefined, true);
				});
			});
	});

	// ///////////////////////////////////////////////////////////
	// Flächennutzung
	// ///////////////////////////////////////////////////////////

	let flaechenSatz = "";

	const frmtPercent = (x) => ("" + x).replace(".", ",") + "%";

	const flaechenNames = {
		parks: "Parks",
		kleingaerten: "Klein&shy;gärten",
		brachen: "Brachen",
		wald: "Wald",
		friedhof: "Fried&shy;höfe",
		sport: "Sport&shy;anlagen",
		landwirtschaft: "land&shy;wirt&shy;schaft&shy;liche Flächen",
	};

	function updateFlaeche(plr) {
		const d = dataFlaechen[plr];

		if (!d) {
			flaechenSatz = "";
			return;
		}

		let bad = "";
		if (d.wohnen_und_gewerbe && d.verkehr) {
			bad = `Wohn- und Gewerbeflächen machen ${frmtPercent(
				d.wohnen_und_gewerbe
			)} des Kiezes aus, für den Verkehr sind ${frmtPercent(d.verkehr)} bestimmt.`;
		} else if (d.wohnen) {
			//bad = `Wohn- und Gewerbeflächen machen ${frmtPercent(d.wohnen_und_gewerbe)} des Kiezes aus, es gibt keine Verkehrsflächen.`;
			bad = `Wohn- und Gewerbeflächen machen ${frmtPercent(d.wohnen_und_gewerbe)} des Kiezes aus.`;
		} else if (d.verkehr) {
			//bad = `Verkehrsflächen machen ${frmtPercent(d.verkehr)} des Kiezes aus, es gibt keine Wohn- und Gewerbeflächen.`;
			bad = `Verkehrsflächen machen ${frmtPercent(d.verkehr)} des Kiezes aus.`;
		}

		let good = "";

		if (d.rest.length > 0) {
			good += "Es gibt im Kiez ";

			d.rest.forEach((x, i) => {
				good += `<b>${frmtPercent(x["value"])} ${flaechenNames[x["type"]]}</b>`;
				if (i === d.rest.length - 1) {
					good += ".";
				} else if (i === d.rest.length - 2) {
					good += " und ";
				} else {
					good += ", ";
				}
			});
		}

		//const water = d.wasser > 0 ? `Wasserflächen machen ${frmtPercent(d.wasser)} aus.` : 'Es gibt keine Gewässer im Kiez.';
		const water = d.wasser > 0 ? `Wasserflächen machen ${frmtPercent(d.wasser)} aus.` : "";

		flaechenSatz = `
        ${bad}
        ${good}
        ${water}
      `;
	}

	// ///////////////////////////////////////////////////////////
	// map selection
	// ///////////////////////////////////////////////////////////
	let marker = null;
	let popup = null;

	const removeMarker = () => {
		if (marker) marker.remove();
	};

	const addMarker = (lon, lat) => {
		if (marker) marker.remove();
		marker = new window.mapboxgl.Marker({
			// anchor: 'bottom',
			color: "#1f1f1f",
			scale: 0.7,
		})
			.setLngLat([lon, lat])
			.addTo(map.map);
	};

	const removePopup = () => {
		if (popup) popup.remove();
	};

	const addPopup = (feature) => {
		const props = feature.properties ? feature.properties : feature;

		if (popup) popup.remove();

		const html = `
				<div class="lor">Planungsraum</div>
        <div class="name">${props.name}</div>
        <div class="bezirk">in ${props.bezirk}</div>
      `;

		popup = new window.mapboxgl.Popup({
			color: "#1f1f1f",
			scale: 0.7,
			closeButton: false,
			closeOnClick: false,
			closeOnMove: false,
			className: "lor-popup",
		})
			.setLngLat([props.centerLng, props.centerLat])
			.setHTML(html)
			.addTo(map.map);
	};

	const findFeature = (lon, lat) => {
		let feature = null;
		// first try mapbox, only works if feature is visible
		// then search in the geometry data
		const point = map.map.project([lon, lat]);

		const fs = map.map.queryRenderedFeatures(point, {
			layers: [geoData.issues.id],
		});

		if (fs.length === 1) {
			feature = fs[0];
		} else {
			const pointf = {
				type: "Feature",
				geometry: {
					type: "Point",
					coordinates: [lon, lat],
				},
			};
			for (let i = 0; i < currentGeometry.features.length; i++) {
				try {
					if (booleanPointInPolygon(pointf, currentGeometry.features[i])) {
						feature = currentGeometry.features[i];
						break;
					}
				} catch (err) {
					console.log(err);
				}
			}
		}
		return feature;
	};

	const selectRegion = (newRegion) => {
		if (!selectedRegion || !newRegion) {
			selectedRegion = newRegion;
		} else {
			const alreadySelected = newRegion === selectedRegion;

			selectedRegion = alreadySelected && !selectedPoint ? undefined : newRegion;
		}

		if (selectedRegion && !selectedRegion.name) selectedRegion.name = selectedRegion[currentGeoData.nameKey];

		stopSearch(selectedRegion ? selectedRegion.name : false);

		updateFlaeche(selectedRegion.plr);
	};

	const clearPoint = () => {
		selectedPoint = null;
		selectedRegion = null;
		hoveredRegion = null;
		tooltip = undefined;
		removeMarker();
		removePopup();
		map.selectChoropleth(geoData.issues.id, false, true);
		selectRegion(false);
	};

	// feature is optional
	const selectPoint = (lngLat, optionalFeature, stayWhereYouAre) => {
		let feature; // = selectedRegion;
		let featureProperties;
		if (lngLat) {
			selectedPoint = lngLat;
			// addMarker(lngLat.lng, lngLat.lat);
		}
		if (optionalFeature) {
			feature = optionalFeature;
		} else if (lngLat) {
			feature = findFeature(lngLat.lng, lngLat.lat);
		} else if (selectedPoint) {
			feature = findFeature(selectedPoint.lng, selectedPoint.lat);
		}
		featureProperties = feature ? feature.properties : undefined;
		if (feature) {
			// D: hier habe ich das false eingefügt - man möchte via adresssuche auch auf kieze kommen die gerade gefiltert sind
			if (false && featureProperties.filtered === true) {
				clearPoint();
				map.toInitialView();
			} else if (featureProperties !== selectedRegion) {
				addPopup(feature);
				selectRegion(featureProperties);
				map.selectChoropleth(geoData.issues.id, null, true);
				map.selectChoropleth(geoData.issues.id, featureProperties.id, stayWhereYouAre);
			}
		} else {
			clearPoint();
		}
	};

	const scrollToTopOfMap = () => {
		scrollTo(elemMap, -90);
	};

	const clearSelection = () => {
		clearPoint();
		scrollToTopOfMap();
	};

	// ///////////////////////////////////////////////////////////
	// markers
	// ///////////////////////////////////////////////////////////

	const berlinMarkers = [];

	function showBerlinMarkers() {
		map.activated
			.then(() => map.loaded)
			.then(() => {
				markerData.forEach((d) => {
					const melc = document.createElement("div");
					const mel = document.createElement("div");
					melc.appendChild(mel);
					mel.classList.add("marker");
					mel.classList.add(`marker--${d.n}`);
					mel.innerHTML = d.label || "";
					berlinMarkers.push(new window.mapboxgl.Marker(melc).setLngLat([d.lon, d.lat]).addTo(map.map));
				});
			});
	}

	function hideBerlinMarkers() {
		map.activated
			.then(() => map.loaded)
			.then(() => {
				berlinMarkers.forEach((m) => {
					m.remove();
				});
			});
	}

	let ringshown = false;
	let ringshownonce = false;
	let ringmarker;
	function showRing() {
		if (ringshown) return;
		ringshown = true;
		if (!ringshownonce) {
			ringshownonce = true;
			map.activated
				.then(() => map.loaded)
				.then(() => {
					map.map.addSource("sbahnring", {
						type: "geojson",
						data: geoRing,
					});
					map.map.addLayer({
						id: "sbahnring1",
						type: "line",
						source: "sbahnring",
						layout: {},
						paint: {
							"line-color": "#ffffff",
							"line-width": 3,
						},
					});
					map.map.addLayer({
						id: "sbahnring2",
						type: "line",
						source: "sbahnring",
						layout: {},
						paint: {
							"line-color": "#1f1f1f",
							"line-width": 2,
							"line-dasharray": [1, 1],
						},
					});
					const pos = [13.277214090669595, 52.52379218491869];
					const el = document.createElement("div");
					el.className = "ring-marker";
					el.innerHTML = '<div class="ring-marker-text">S-Bahn-Ring</div>';
					ringmarker = new window.mapboxgl.Marker(el).setLngLat(pos);
					ringmarker.addTo(map.map);
				});
		} else {
			map.map.setLayoutProperty("sbahnring1", "visibility", "visible");
			map.map.setLayoutProperty("sbahnring2", "visibility", "visible");
			ringmarker.addTo(map.map);
		}
	}

	function hideRing() {
		if (!ringshown) return;
		map.map.setLayoutProperty("sbahnring1", "visibility", "none");
		map.map.setLayoutProperty("sbahnring2", "visibility", "none");
		ringmarker.remove();
		ringshown = false;
	}

	// ///////////////////////////////////////////////////////////
	// search
	// ///////////////////////////////////////////////////////////
	let stopSearch;

	// ///////////////////////////////////////////////////////////
	// tooltip
	// ///////////////////////////////////////////////////////////

	const closeClick = () => clearPoint();

	function tooltipClick() {
		// clearPoint();
	}

	let tooltipExpanded = true;

	function tooltipToggle(e) {
		if (window.innerWidth > 544) return;
		e.stopPropagation();
		tooltipExpanded = !tooltipExpanded;
	}

	function tooltipMouseover() {
		if (window.innerWidth <= 544) return;
		tooltipHovered = true;
		if (selectedRegion) {
			hoveredRegion = undefined;
			removePopup();
			if (selectedRegion) addPopup(selectedRegion);
		}
	}

	function tooltipMouseout() {
		if (window.innerWidth <= 544) return;
		tooltipHovered = false;
	}

	let breakpoint;
	let platform;
	let orientation;
</script>

<MediaQuery bind:breakpoint bind:platform bind:orientation />
<div class="gruenflaechen-map" class:pw={paywall}>
	<div class="headline">Finde deinen Kiez</div>
	<div class="ui-container-top">
		<div class="buttons-and-filter">
			<Buttons bind:value={selectedIssue} options={environmentalButtonOptions} let:option let:active>
				<div class="button-main" class:active>
					<img src="{PATH}assets/images/icons/{option.icon}" alt="" />
					<span data-title={option.label}>{option.label}</span>
				</div>
			</Buttons>
			<div class="filter-social">
				<!-- <div class="line" /> -->
				<div class="filter-label">
					<img src="{PATH}assets/images/icons/filter.svg" alt="" />
					<span>Sozialstatus filtern</span>
				</div>
				{#each socialButtonOptions as o}
					{@const selected = selectedSocials.includes(o.value)}
					<div
						class="filter-option"
						class:selected
						on:click={() => {
							if (selectedSocials.includes(o.value)) selectedSocials = selectedSocials.filter((d) => d !== o.value);
							else selectedSocials = [...selectedSocials, o.value];
						}}
					>
						<div class="checkbox">
							{#if selected}
								<img src="{PATH}assets/images/icons/checkmark_w.svg" alt="" />
							{/if}
						</div>
						<div class="text">{o.label}</div>
					</div>
				{/each}
				<InfoBox content={socialStatus.infobox} />
			</div>
		</div>
		<div class="search">
			<Search
				type={["geo"]}
				icon="pin"
				geoSearchBounds={[initialBounds[1], initialBounds[0]]}
				dataSearchData={currentGeometry}
				dataSearchKeys={[geoData.issues.nameKey]}
				dataSearchGetLabel={(d) => d[geoData.issues.nameKey]}
				placeholder="Kiez, Adresse, Ort eingeben"
				clearCallback={() => clearPoint()}
				resultCallback={(d) => {
					addMarker(d.lon, d.lat);
					selectPoint({ lng: d.lon, lat: d.lat });
				}}
				bind:stopSearch
			/>
		</div>
	</div>
	<div
		class="map-container"
		class:tooltip-shown={tooltip}
		on:mouseleave={() => {
			hoveredRegion = undefined;
			removePopup();
			if (selectedRegion) addPopup(selectedRegion);
		}}
	>
		<div class="map" bind:this={elemMap} />

		{#if paywall}
			<div class="pw-map-image" />
		{/if}

		<div
			class="button-luftbilder"
			class:hidden={mapZoom < detailedGreeneryZoom && !showLuftbilder}
			class:luftbilder={showLuftbilder}
			on:click={() => {
				showLuftbilder = !showLuftbilder;
			}}
		>
			<div class="button-luftbilder-image button-luftbilder-image-on" />
			<div class="button-luftbilder-image button-luftbilder-image-off" />
			<div class="button-luftbilder-text mobile">
				{showLuftbilder ? "Straßen-karte" : "Luft-bilder"}
			</div>
			<div class="button-luftbilder-text desktop">
				{showLuftbilder ? "Auf Straßenkarte umschalten" : "Auf Luftbilder umschalten"}
			</div>
		</div>
		<div class="legend" class:invert-colors={showLuftbilder}>
			<div class="legend-title">{issuesAndTotalById[selectedIssue].label}</div>
			<div class="legend-items">
				{#each issuesAndTotalById[selectedIssue].values as v}
					<div class="legend-item">
						<div class="legend-symbol" style:background={v.color} />
						<div class="legend-label">{v.value}</div>
					</div>
				{/each}
			</div>
		</div>
		<div class="map-loading" class:mapLoaded>
			<div class="ts-spinner">
				<div class="ts-double-bounce1" />
				<div class="ts-double-bounce2" />
			</div>
		</div>
		{#if selectedRegion || (hoveredRegion && !hoveredRegion.filtered)}
			{@const region = hoveredRegion || selectedRegion}
			{@const hoveredAndSelectedAreSame =
				hoveredRegion && selectedRegion ? hoveredRegion.plr === selectedRegion.plr : false}
			{@const isSelected = (!hoveredRegion || hoveredAndSelectedAreSame) && !!selectedRegion}
			{@const regionSocial = region[socialStatus.id]}
			<div
				class="tooltip"
				on:click={tooltipClick}
				on:mouseover={tooltipMouseover}
				on:focus={() => {}}
				on:blur={() => {}}
				on:mouseout={tooltipMouseout}
				class:expanded={tooltipExpanded}
				transition:fly={{ y: 100, duration: platform === "mobile" ? 400 : 0 }}
				bind:this={tooltip}
			>
				<div class="tooltip-top">
					<div class="title-and-bezirk">
						<div class="lor">Planungsraum</div>
						<div class="title">{@html region.name.replace("/", "/&#8203;")}</div>
						<div class="bezirk">in {region.bezirk}</div>
					</div>
					{#if isSelected}
						<div class="button-close" on:click={closeClick} />
					{/if}
				</div>
				<div class="social">
					{socialStatus.label}:
					<span class="value" style:color={!regionSocial ? colorNoData : ""}
						>{!regionSocial
							? labelNoData
							: socialStatus.labels && socialStatus.labels[regionSocial]
							? socialStatus.labels[regionSocial]
							: regionSocial}</span
					>
				</div>
				<div class="rule" />
				{#each issuesAndTotal as e}
					{@const value = region[e.id]}
					{@const color = e.id !== issuesTotalKey ? assessments[value].color : null}
					{@const iconvalue = e.id !== issuesTotalKey ? assessments[value].icon : null}
					{@const iconlabel = e.icon}
					{#if e.id === issuesTotalKey}
						<div class="rule" />
					{/if}
					<div class="row" class:rowSelected={e.id === selectedIssue}>
						{#if iconlabel}
							<span class="icon-label" style="background-image: url({PATH}assets/images/icons/{iconlabel}.svg);" />
						{/if}
						<span class="label">{e.label}:</span>
						<span class="value" style:color>
							{value === null || value === "null" ? labelNoData : e.labels && e.labels[value] ? e.labels[value] : value}
						</span>
						{#if iconvalue}
							<span class="icon-value" style="background-image: url({PATH}assets/images/icons/{iconvalue}.svg);" />
						{/if}
						{#if e.id === issuesTotalKey}
							{#each issues as e}
								{#if ["hoch", "schlecht"].indexOf(region[e.id]) > -1}
									<span class="icon-bad" style="background-image: url({PATH}assets/images/icons/{e.icon}_r.svg);" />
								{/if}
							{/each}
						{/if}
					</div>
				{/each}
				<div class="rule" />
				<div class="flaeche">{@html flaechenSatz}</div>
			</div>
		{/if}
	</div>
	<div class="attribution">
		Daten:&nbsp;<a href="https://www.berlin.de/sen/uvk/umwelt/nachhaltigkeit/umweltgerechtigkeit/" target="_blank"
			>Umweltgerechtigkeitsatlas 2021/22</a
		>
		Kartenbasis:&nbsp;<a href="https://www.openmaptiles.org/" target="_blank">&copy;&nbsp;OpenMapTiles</a>
		<a href="https://www.openstreetmap.org/copyright" target="_blank">&copy;&nbsp;OpenStreetMap contributors</a>
		{#if showLuftbilder}
			| <a
				target="_blank"
				href="https://fbinter.stadt-berlin.de/fb/berlin/service.jsp?id=a_luftbild2022_true_rgbi@senstadt&amp;type=FEED"
				>Geoportal Berlin / Digitale farbige Orthophotos 2022 (DOP20RGBI)</a
			>
		{/if}
	</div>

	{#if paywall}
		<div class="pw-overlay" />
	{/if}
</div>

<style lang="scss">
	@import "./main.scss";

	$tooltip-offset: 25vh;

	.gruenflaechen-map {
		font-family: $ff-sans;
		font-size: $fs-base;
		line-height: $lh-base;
		color: $color;
		user-select: none;
	}

	.map-container {
		position: relative;
		margin-bottom: 0;
		transition: margin 0.3s ease;
	}

	.attribution {
		font-size: 14px;
		color: #666;
		line-height: 1.5em;
		margin-top: 12px;
		text-align: right;

		a {
			text-decoration: none;
		}
	}

	.map {
		position: relative;
		height: 650px;
	}

	.ui-container-top {
		display: flex;
		justify-content: space-between;
		margin-bottom: 5px;

		@media (max-width: $breakpoint-tablet-l) {
			flex-direction: column-reverse;
			margin-bottom: 5px;

			.search {
				width: 100%;
				margin-bottom: 8px;
			}
		}

		@media (min-width: #{$breakpoint-tablet-s + 1px}) and (max-width: $breakpoint-tablet-l) {
			align-items: center;

			.search {
				width: 635px;
			}
		}

		@media (min-width: #{$breakpoint-tablet-l + 1px}) {
			.search {
				flex-basis: 250px;
			}
		}
	}

	.ui-container-bottom {
		display: flex;
		justify-content: space-between;
	}

	.headline {
		font-weight: $bold;
		font-size: 16px;
		text-align: center;
		margin-bottom: 10px;

		@media (min-width: #{$breakpoint-tablet-l + 1px}) {
			display: none;
		}
	}

	.filter-social {
		display: flex;
		flex-direction: row;
		font-size: 13px;
		font-weight: $bold;
		align-items: center;

		@media (max-width: $breakpoint-mobile-s) {
			flex-wrap: wrap;
		}

		@media (max-width: $breakpoint-mobile-l) {
			.line {
				display: none;
			}

			.filter-label {
				margin-right: 10px !important;
			}
		}

		.line {
			width: 10px;
			height: 16px;
			border-left: 2px solid #7a7a7a;
			border-bottom: 2px solid #7a7a7a;
			border-radius: 0 0 0 3px;
			margin: 0 5px;
			align-self: flex-start;
		}

		.filter-label {
			margin-right: 20px;

			img {
				vertical-align: sub;
				width: fit-content;
				display: inline;
			}
		}

		.filter-option {
			color: #a5a5a5;
			display: flex;
			flex-direction: row;
			margin-right: 15px;
			font-weight: 500;
			padding: 11px 0;

			@media (max-width: $breakpoint-mobile-m) {
				margin-right: 10px;
			}

			&:nth-last-child(2) {
				margin-right: 8px;
			}

			.checkbox {
				width: 12px;
				height: 12px;
				background: #a5a5a5;
				border-radius: 2px;
				margin-right: 8px;

				img {
					width: 100%;
					height: 100%;
				}
			}
		}

		.filter-option.selected {
			color: $black;

			.checkbox {
				background: #272727;
			}
		}
	}

	// desktop
	@media (min-width: #{$breakpoint-tablet-l + 1px}) {
	}

	// large tablets
	@media (min-width: #{$breakpoint-tablet-s + 1px}) and (max-width: $breakpoint-tablet-l) {
		.map {
			height: 600px;
		}
	}

	// small tablets
	@media (min-width: #{$breakpoint-mobile-l + 1px}) and (max-width: $breakpoint-tablet-s) {
		.map {
			height: 600px;
		}
	}

	// large/normal phones
	@media (min-width: #{$breakpoint-mobile-m + 1px}) and (max-width: $breakpoint-mobile-l) {
		.map {
			height: 460px;
		}
	}

	// small phones
	@media (min-width: #{$breakpoint-mobile-s + 1px}) and (max-width: $breakpoint-mobile-m) {
		.map {
			height: 420px;
		}
	}

	// very small phones
	@media (max-width: $breakpoint-mobile-s) {
		.map {
			height: 420px;
		}
	}

	@media (max-width: 600px) {
		// :global(.tsg-map__controls) {
		// 	bottom: 30px !important;
		// }
	}

	:global(.mapboxgl-ctrl-scale) {
		bottom: -4px !important;
	}

	:global(.marker) {
		pointer-events: none;
		background-position: center bottom;
		background-size: contain;
		background-repeat: no-repeat;
		position: absolute;
		bottom: 0;
		left: 0;
		transform: translateX(-50%);
		font-weight: $bold;
		font-size: 11px;
		font-family: $ff-sans;
		line-height: 14px;
		color: #fff;
		text-shadow: 1px 1px #1f1f1f, -1px 1px #1f1f1f, -1px -1px #1f1f1f, 1px -1px #1f1f1f, 0 1px #1f1f1f, 0 -1px #1f1f1f,
			1px 0 #1f1f1f, -1px 0 #1f1f1f, 0 0 2px rgba(0, 0, 0, 0.1);
	}

	:global(.marker.marker--bbtor) {
		background-image: url(../assets/images/berlin/bbtor.svg);
		width: 26px;
		height: 25px;
	}

	:global(.marker.marker--siegessaeule) {
		display: none;
		background-image: url(../assets/images/berlin/siegessaeule.svg);
		width: 30px;
		height: 30px;
	}

	:global(.marker.marker--fernsehturm) {
		background-image: url(../assets/images/berlin/fernsehturm.svg);
		height: 45px;
		width: 20px;
	}

	:global(.marker.marker--kaiserwilhelm) {
		background-image: url(../assets/images/berlin/kaiserwilhelm.svg);
		width: 30px;
		height: 30px;
	}

	:global(.marker.marker--oberbaum) {
		background-image: url(../assets/images/berlin/oberbaum.svg);
		width: 35px;
		height: 30px;
	}

	:global(.ring-marker-text) {
		position: absolute;
		// left: -20px;
		// top: -20px;
		font-family: $ff-sans;
		white-space: nowrap;
		font-size: 11px;
		color: #272727;
		font-weight: $bold;
		text-shadow: 1px 1px #fff, -1px 1px #fff, 1px -1px #fff, -1px -1px #fff;
		transform: translate(-100%, -100%) !important;
		pointer-events: none;

		// &::before {
		// 	content: " ";
		// 	width: 20px;
		// 	height: 20px;
		// 	position: absolute;
		// 	left: calc(100% + 4px);
		// 	top: 50%;
		// 	background-image: url(../assets/images/arrow_mobile.svg);
		// 	background-size: contain;
		// 	background-repeat: no-repeat;
		// 	background-position: center center;
		// 	transform: scaleX(-1);
		// }
	}

	:global(.lor-popup) {
		pointer-events: none !important;
	}

	:global(.lor-popup .mapboxgl-popup-content) {
		border-radius: 4px;
		font-family: $ff-sans;
		text-align: center;
		padding: 7px 9px !important;
		pointer-events: none !important;
	}

	:global(.lor-popup .lor) {
		font-weight: $bold;
		font-size: 9px;
		line-height: 13px;
		letter-spacing: 0.085em;
		text-transform: uppercase;
		color: #747474;
	}

	:global(.lor-popup .name) {
		font-weight: $bold;
		letter-spacing: 0.015em;
		font-size: 13px;
		line-height: 14px;
	}

	:global(.lor-popup .bezirk) {
		letter-spacing: 0.015em;
		font-size: 12px;
		line-height: 15px;
	}

	// very phones
	@media (max-width: $breakpoint-mobile-l) {
		:global(.marker) {
			font-size: 11px;
			line-height: 13px;
		}

		:global(.marker--bbtor) {
			width: 21px;
			height: 20px;
		}

		:global(.marker--siegessaeule) {
			width: 24px;
			height: 24px;
		}

		:global(.marker--fernsehturm) {
			height: 36px;
			width: 16px;
		}

		:global(.marker--kaiserwilhelm) {
			width: 24px;
			height: 24px;
		}

		:global(.marker--oberbaum) {
			width: 28px;
			height: 24px;
		}

		:global(.ring-marker-text) {
			display: none;
		}
	}

	// Anpassungen mapcontrols und scale
	// loading spinner

	.map {
		z-index: 0;
	}

	.map-loading {
		position: absolute;
		background: #f2f2f2;
		left: 0;
		top: 0;
		bottom: 0;
		right: 0;
		pointer-events: all;
		z-index: 1;
		transition: opacity 0.2s ease;
		opacity: 1;

		&.mapLoaded {
			opacity: 0;
			pointer-events: none;
		}
	}

	.ts-spinner {
		width: 40px;
		height: 40px;
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
	}

	.ts-double-bounce1,
	.ts-double-bounce2 {
		width: 100%;
		height: 100%;
		border-radius: 50%;
		background-color: #333;
		opacity: 0.6;
		position: absolute;
		top: 0;
		left: 0;
		animation: sk-bounce 2s infinite ease-in-out;
	}

	.ts-double-bounce2 {
		animation-delay: -1s;
	}

	@keyframes sk-bounce {
		0%,
		100% {
			transform: scale(0);
			-webkit-transform: scale(0);
		}

		50% {
			transform: scale(1);
			-webkit-transform: scale(1);
		}
	}

	// LEGENDE

	.legend {
		font-size: 13px;
		line-height: 20px;
		white-space: nowrap;
		// padding: 5px 10px;
		display: flex;
		flex-direction: row;
		// background: rgba(255, 255, 255, 0.9);
		position: absolute;
		top: 6px;
		left: 10px;
		border-radius: 3px;
		text-shadow: -1px 1px 0 $white, 1px 1px 0 $white, 1px -1px 0 $white, -1px -1px 0 $white;
		@media (min-width: #{$breakpoint-mobile-l + 1px}) {
			&.invert-colors {
				color: $white;
				text-shadow: -1px 1px 0 $black, 1px 1px 0 $black, 1px -1px 0 $black, -1px -1px 0 $black;

				.legend-symbol {
					border: 1px solid $black;
				}
			}
		}
	}

	.legend-title {
		font-weight: 600;
	}

	.legend-item {
		display: inline-block;

		&:last-child {
			margin-right: 0;
		}
	}

	@media (max-width: $breakpoint-mobile-l) {
		.legend-title {
			margin-right: 5px;
		}

		.legend-item {
			margin-right: 5px;
		}
	}

	@media (min-width: #{$breakpoint-mobile-l + 1px}) {
		.legend-title {
			margin-right: 15px;
		}

		.legend-item {
			margin-right: 10px;
		}
	}

	.legend-symbol {
		display: inline-block;
		width: 8px;
		height: 8px;
		border: 1px solid $white;
		// box-shadow: 0 0 2px rgba(0, 0, 0, 0.25);
		margin-right: 2px;
	}

	.legend-label {
		display: inline-block;
	}

	@media (max-width: $breakpoint-mobile-l) {
		.legend {
			position: relative;
			background: none;
			padding: 10px 0 10px 0;
			left: 0;
			top: 0;
			display: flex;
			justify-content: space-between;
			flex-wrap: wrap;
		}
	}

	// TOOLTIP

	.tooltip {
		background: white;
		padding: 17px 10px 25px 15px;
		position: absolute;
		top: 100px;
		right: 10px;
		font-size: 14px;
		line-height: 20px;
		border-radius: 3px;
		box-shadow: 0 4px 5px rgba(0, 0, 0, 0.15);
		width: 270px;
		box-sizing: border-box;
		z-index: 1;

		.rule {
			height: 1px;
			background: #cdcdcd;
			margin: 8px 0;
		}

		.tooltip-top {
			display: flex;
			justify-content: space-between;
		}

		.button-close {
			cursor: pointer;
			background-position: center center;
			background-image: url(../assets/images/icons/icon_close.svg);
			width: 20px;
			height: 20px;
			background-size: contain;
			margin-right: 5px;
		}

		.lor {
			color: #8b8b8b;
			font-weight: 400;
			font-size: 11px;
			line-height: 11px;
			letter-spacing: 1px;
			text-transform: uppercase;
			margin-bottom: 3px;
		}

		.title {
			font-weight: 600;
			font-size: 18px;
			line-height: 21px;
			margin-bottom: 5px;
		}

		.bezirk {
			line-height: 12px;
			margin-bottom: 12px;
		}

		.value {
			font-weight: $medium;
		}

		.social {
			color: #8b8b8b;
			line-height: 14px;
		}

		.flaeche {
			:global(b) {
				color: #6b6b6b;
				font-weight: 600;
			}
		}

		.icon-label {
			display: inline-block;
			width: 16px;
			height: 16px;
			background-position: center center;
			background-repeat: no-repeat;
			background-size: contain;
			position: relative;
			top: 3px;
			opacity: 0.5;
		}

		.icon-value {
			display: inline-block;
			width: 14px;
			height: 14px;
			background-position: center center;
			background-repeat: no-repeat;
			background-size: contain;
			position: relative;
			top: 3px;
		}

		.icon-bad {
			display: inline-block;
			width: 16px;
			height: 16px;
			background-position: center center;
			background-repeat: no-repeat;
			background-size: 12px;
			position: relative;
			top: 3px;
			background-color: #fcece9;
			border-radius: 50%;
			margin: -1px 1px;
		}

		.row {
			white-space: nowrap;
		}

		.rowSelected {
			.label {
				font-weight: 600;
			}

			.icon-label {
				opacity: 1;
			}
		}

		@media (max-width: $breakpoint-mobile-l) {
			// position: relative;
			// top: -$tooltip-offset;
			position: absolute;
			top: calc(100% - #{$tooltip-offset});
			width: 100%;
			left: 0;
			right: 0;
			border-radius: 10px;
			box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
			padding: 10px 15px;

			.title,
			.bezirk {
				display: inline;
			}

			.button-close {
				margin-right: 0px;
			}

			.title-and-bezirk {
				position: relative;
				padding-right: 20px;
				margin-bottom: 8px;
			}

			&:not(.expanded) {
				.rule,
				.flaeche,
				.social,
				.row {
					display: none;
				}

				.rowSelected {
					display: block;

					.label {
						font-weight: normal;
					}
				}
			}
		}
	}

	@media (min-width: 501px) and (max-width: $breakpoint-mobile-l) {
		$tooltip-height: 65vw;

		.tooltip {
			height: $tooltip-height;
		}
		.map-container.tooltip-shown {
			margin-bottom: calc(#{$tooltip-height} - 0.6 * #{$tooltip-offset});
		}
	}

	@media (min-width: 481px) and (max-width: 500px) {
		$tooltip-height: 70vw;

		.tooltip {
			height: $tooltip-height;
		}
		.map-container.tooltip-shown {
			margin-bottom: calc(#{$tooltip-height} - 0.6 * #{$tooltip-offset});
		}
	}

	@media (min-width: 401px) and (max-width: 480px) {
		$tooltip-height: calc(5vw + 350px);

		.tooltip {
			height: $tooltip-height;
		}
		.map-container.tooltip-shown {
			margin-bottom: calc(#{$tooltip-height} - 0.6 * #{$tooltip-offset});
		}
	}

	@media (max-width: 400px) {
		$tooltip-height: calc(10vw + 350px);

		.tooltip {
			height: $tooltip-height;
		}
		.map-container.tooltip-shown {
			margin-bottom: calc(#{$tooltip-height} - 0.6 * #{$tooltip-offset});
		}
	}

	// LUFTBILDER BUTTON

	.button-luftbilder {
		width: 48px;
		height: 48px;
		background: #fff;
		border-radius: 4px;
		box-shadow: 0 3px 5px rgba(0, 0, 0, 0.25);
		position: absolute;
		top: 20px;
		right: 20px;

		&.hidden {
			display: none;
		}
	}

	.button-luftbilder-image {
		position: absolute;
		left: 4px;
		top: 4px;
		width: calc(100% - 8px);
		height: calc(100% - 8px);
		border-radius: 3px;
		background-size: cover;
		background-position: center center;
		overflow: hidden;
	}

	.button-luftbilder-image-on {
		display: none;
		background-image: url(../assets/images/icons/icon_data_layer.jpg);
	}

	.button-luftbilder-image-off {
		background-image: url(../assets/images/icons/icon_satellite_layer.jpg);
		display: block;
	}

	.button-luftbilder.luftbilder {
		.button-luftbilder-image-on {
			display: block;
		}

		.button-luftbilder-image-off {
			display: none;
		}
	}

	.button-luftbilder-text {
		color: #6a6a6a;
		font-weight: 600;
		font-size: 11px;
		line-height: 12px;
		text-align: center;
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translate(-50%, 5px);
		text-shadow: -1px 1px 0 #fff, 1px 1px 0 #fff, 1px -1px 0 #fff, -1px -1px 0 #fff;

		&.desktop {
			width: 84px;
		}
	}

	@media (min-width: #{$breakpoint-mobile-l + 1px}) {
		.button-luftbilder-text.mobile {
			display: none;
		}
	}

	@media (max-width: $breakpoint-mobile-l) {
		.button-luftbilder {
			width: 38px;
			height: 38px;
			top: 10px;
			right: 10px;
		}

		.button-luftbilder-image {
			left: 3px;
			top: 3px;
			width: calc(100% - 6px);
			height: calc(100% - 6px);
		}

		.button-luftbilder-text.desktop {
			display: none;
		}
	}

	// buttons
	.button-main {
		font-weight: 500;
		text-align: center;
		display: flex;
		align-items: center;

		img {
			height: 26px;
			margin-right: 5px;
			width: unset;
		}

		span::after {
			content: attr(data-title);
			font-weight: 600;
			display: block;
			height: 0px;
			visibility: hidden;
		}

		&.active {
			font-weight: 600;
		}
	}

	.pw-overlay {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		top: 0;
		background: transparent;
	}

	.pw-map-image {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		top: 0;
		background-image: url(../assets/images/pw-map-image.jpg);
		background-size: cover;
		background-repeat: no-repeat;
		background-position: right center;
		filter: blur(2px);

		@media (min-width: #{$breakpoint-mobile-l + 1px}) and (max-width: $breakpoint-tablet-l) {
			background-position: right top;
			background-image: url(../assets/images/pw-map-image-m.jpg);
		}

		@media (max-width: $breakpoint-mobile-l) {
			background-position: center center;
			background-image: url(../assets/images/pw-map-image-s.jpg);
		}
	}
	@media (max-width: $breakpoint-mobile-l) {
		.pw {
			.map {
				height: 144vw !important;
			}
			.legend {
				display: none;
			}
		}
	}
</style>
